import { useEffect, useMemo, useState } from "react";

const PAGE_SIZE = 100;

export const useSummaryDetail = ({
  activeMetricId,
  selectedAccount,
  selectedMarketplace,
  authFetch,
  detailCache,
  setDetailCache,
  setTableCache,
}) => {
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const metricDetailKey = useMemo(() => {
    return `${selectedAccount}__${selectedMarketplace}__${activeMetricId}`;
  }, [selectedAccount, selectedMarketplace, activeMetricId]);

  useEffect(() => {
    const loadDetail = async () => {
      if (!selectedAccount || !selectedMarketplace || !activeMetricId) {
        setLoadingDetail(false);
        setDetailError(null);
        return;
      }

      if (detailCache[metricDetailKey]) {
        setLoadingDetail(false);
        setDetailError(null);
        return;
      }
      
      try {
        setLoadingDetail(true);
        setDetailError(null);

        const response = await authFetch(
          `/amz-api/dashboard/detail/${activeMetricId}`,
          {
            method: "POST",
            body: JSON.stringify({
              account: selectedAccount,
              marketplace: selectedMarketplace,
              page: 1,
              pageSize: PAGE_SIZE,
              search: "",
              tableOnly: false,
            }),
          }
        );

        if (!response) return;

        const data = await response.json();
        const tablePayload = data.table || null;

        setDetailCache((prev) => ({
          ...prev,
          [metricDetailKey]: data,
        }));

        const firstTableKey = `${selectedAccount}__${selectedMarketplace}__${activeMetricId}__1__`;
        setTableCache((prev) => ({
          ...prev,
          [firstTableKey]: tablePayload,
        }));
      } catch (error) {
        setDetailError("Failed to load detail data");
      } finally {
        setLoadingDetail(false);
      }
    };

    loadDetail();
  }, [
    activeMetricId,
    selectedAccount,
    selectedMarketplace,
    metricDetailKey,
    detailCache,
    authFetch,
    setDetailCache,
    setTableCache,
  ]);

  const activeDetail = detailCache[metricDetailKey] || null;
  const historyData = activeDetail?.history || [];

  return {
    loadingDetail,
    detailError,
    activeDetail,
    historyData,
    metricDetailKey,
  };
};
